import { IStatMetric, IBarData, ChartValue } from "../types/aiMetrics.types";
import {
  AdoKpiCard,
  AdoDashboardResult,
  AdoMetricResult,
  AdoSprintSeries,
} from "../api/aiMetricsApi";
import { SERIES_COLORS } from "./echartOptions";

export interface ISprintTeamSeries {
  team: string;
  current: ChartValue[];
  prior: ChartValue[];
  priorLabels: (string | null)[];
  color: string;
}

export interface ISprintChartData {
  labels: string[];
  teams: ISprintTeamSeries[];
  currentLabel: string;
  priorLabel: string;
}

const MONTH_LABEL = /^\d{4}-\d{2}$/;

function naturalSprintSort(a: string, b: string): number {
  // Calendar-month labels ("YYYY-MM", e.g. cycle-time trend) are chronological
  // under a plain lexicographic compare; the sprint-number heuristic below would
  // sort them by month digits and ignore the year.
  if (MONTH_LABEL.test(a) && MONTH_LABEL.test(b)) return a.localeCompare(b);
  const na = Number((a.match(/(\d+)\s*$/) || [])[1]);
  const nb = Number((b.match(/(\d+)\s*$/) || [])[1]);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b);
}

function seriesToMap(s?: AdoSprintSeries): Map<string, number> {
  const m = new Map<string, number>();
  if (!s) return m;
  s.labels.forEach((label, i) => {
    const v = s.values[i];
    if (typeof v === "number") m.set(label, v);
  });
  return m;
}

function seriesToOrdered(
  s?: AdoSprintSeries,
): { label: string; value: ChartValue }[] {
  if (!s) return [];
  return s.labels
    .map((label, i) => ({
      label,
      value: typeof s.values[i] === "number" ? s.values[i] : null,
    }))
    .sort((a, b) => naturalSprintSort(a.label, b.label));
}

function alignPriorFromEnd(
  prior: { label: string; value: ChartValue }[],
  slots: number,
): { values: ChartValue[]; labels: (string | null)[] } {
  const values: ChartValue[] = new Array(slots).fill(null);
  const labels: (string | null)[] = new Array(slots).fill(null);
  const pairs = Math.min(slots, prior.length);
  for (let k = 1; k <= pairs; k++) {
    const slotIdx = slots - k;
    const priorIdx = prior.length - k;
    values[slotIdx] = prior[priorIdx].value;
    labels[slotIdx] = prior[priorIdx].label;
  }
  return { values, labels };
}

export function adaptSprintChart(
  currentResults: AdoMetricResult[],
  priorResults: AdoMetricResult[],
  currentLabel: string,
  priorLabel: string,
): ISprintChartData {
  const labelSet = new Set<string>();
  for (const r of currentResults) {
    for (const l of r.sprint_series?.labels ?? []) labelSet.add(l);
  }
  const labels = [...labelSet].sort(naturalSprintSort);

  const priorByTeam = new Map(
    priorResults.map((r) => [r.team_name, seriesToOrdered(r.sprint_series)]),
  );

  const teams: ISprintTeamSeries[] = currentResults.map((r, i) => {
    const cur = seriesToMap(r.sprint_series);
    const prior = alignPriorFromEnd(
      priorByTeam.get(r.team_name) ?? [],
      labels.length,
    );
    return {
      team: r.team_name,
      current: labels.map((l) => (cur.has(l) ? cur.get(l)! : null)),
      prior: prior.values,
      priorLabels: prior.labels,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
    };
  });

  return { labels, teams, currentLabel, priorLabel };
}

export function sprintChartToBarData(
  data: ISprintChartData,
  comparisonEnabled: boolean,
): IBarData {
  const sumAt = (key: "current" | "prior", idx: number): ChartValue => {
    const vals = data.teams
      .map((t) => t[key][idx])
      .filter((v): v is number => typeof v === "number");
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };

  // The prior sprint name shown in tooltips (first team that has one here).
  const priorLabelAt = (idx: number): string | null =>
    data.teams.find((t) => t.priorLabels[idx] != null)?.priorLabels[idx] ??
    null;

  const current = data.labels.map((_, i) => sumAt("current", i));
  if (!comparisonEnabled) {
    return {
      labels: data.labels,
      series: [{ name: data.currentLabel, values: current }],
    };
  }
  const prior = data.labels.map((_, i) => sumAt("prior", i));
  const priorLabels = data.labels.map((_, i) => priorLabelAt(i));
  return {
    labels: data.labels,
    series: [
      { name: data.priorLabel, values: prior, pointLabels: priorLabels },
      { name: data.currentLabel, values: current },
    ],
  };
}

function formatValue(value: number | null, unit: string): string | number {
  if (value === null) return "—";
  return unit ? `${value}${unit}` : value;
}

function getCards(
  results: AdoDashboardResult[],
  section: "velocity_throughput" | "quality" | "delivery_progress",
  key: string,
): AdoKpiCard[] {
  return results
    .map((r) => (r[section] as Record<string, AdoKpiCard>)[key])
    .filter((c): c is AdoKpiCard => c != null);
}

function aggDelta(
  cards: AdoKpiCard[],
  comparison: boolean,
): number | undefined {
  if (!comparison) return undefined;
  const withDelta = cards.filter((c) => c.change_pct !== null);
  if (!withDelta.length) return undefined;
  const avg =
    withDelta.reduce((acc, c) => acc + (c.change_pct as number), 0) /
    withDelta.length;
  return Math.round(avg * 10) / 10;
}

function aggSum(
  results: AdoDashboardResult[],
  section: "velocity_throughput" | "quality" | "delivery_progress",
  key: string,
  comparison = true,
): IStatMetric {
  const cards = getCards(results, section, key);
  if (!cards.length) return { value: "—" };
  const unit = cards[0].unit;
  const nonNull = cards.filter((c) => c.value !== null);
  if (!nonNull.length) return { value: "—" };
  const total = nonNull.reduce((acc, c) => acc + (c.value as number), 0);
  return {
    value: formatValue(Math.round(total), unit),
    delta: aggDelta(nonNull, comparison),
  };
}

function aggAvg(
  results: AdoDashboardResult[],
  section: "velocity_throughput" | "quality" | "delivery_progress",
  key: string,
  comparison = true,
  decimals = 1,
): IStatMetric {
  const cards = getCards(results, section, key);
  if (!cards.length) return { value: "—" };
  const unit = cards[0].unit;
  const nonNull = cards.filter((c) => c.value !== null);
  if (!nonNull.length) return { value: "—" };
  const avg =
    nonNull.reduce((acc, c) => acc + (c.value as number), 0) / nonNull.length;
  const rounded = Math.round(avg * 10 ** decimals) / 10 ** decimals;
  return {
    value: formatValue(rounded, unit),
    delta: aggDelta(nonNull, comparison),
  };
}

function aggregateSeries(
  r: AdoMetricResult,
  aggregate: "sum" | "avg",
): number | null {
  const vals = (r.sprint_series?.values ?? []).filter(
    (v): v is number => typeof v === "number",
  );
  if (!vals.length) return null;
  const total = vals.reduce((a, b) => a + b, 0);
  return aggregate === "avg" ? total / vals.length : total;
}

function sprintLeaf(fullLabel: string): string {
  const parts = fullLabel.split("\\");
  return parts[parts.length - 1].trim() || fullLabel;
}

export function adaptBySprint(
  currentResults: AdoMetricResult[],
): ISprintChartData {
  const labelSet = new Set<string>();
  for (const r of currentResults) {
    for (const l of r.sprint_series?.labels ?? []) labelSet.add(l);
  }

  const fullLabels = [...labelSet].sort(naturalSprintSort);
  const labels = fullLabels.map(sprintLeaf);

  const teams: ISprintTeamSeries[] = currentResults.map((r, i) => {
    const cur = seriesToMap(r.sprint_series);
    return {
      team: r.team_name,
      current: fullLabels.map((l) => (cur.has(l) ? cur.get(l)! : null)),
      prior: [],
      priorLabels: [],
      color: SERIES_COLORS[i % SERIES_COLORS.length],
    };
  });

  return { labels, teams, currentLabel: "", priorLabel: "" };
}

export function adaptByTeamComparison(
  currentResults: AdoMetricResult[],
  priorResults: AdoMetricResult[],
  currentLabel: string,
  priorLabel: string,
  aggregate: "sum" | "avg" = "sum",
): IBarData {
  const priorMap = new Map(
    priorResults.map((r) => [r.team_name, aggregateSeries(r, aggregate)]),
  );
  const rows = currentResults.map((r) => ({
    team_name: r.team_name,
    current: aggregateSeries(r, aggregate),
    prior: priorMap.get(r.team_name) ?? null,
  }));
  return {
    labels: rows.map((r) => r.team_name),
    series: [
      { name: priorLabel, values: rows.map((r) => r.prior) },
      { name: currentLabel, values: rows.map((r) => r.current) },
    ],
  };
}

// --- KPI card adapters ---

export interface AdaptedVelocity {
  throughput: IStatMetric;
  commitmentReliability: IStatMetric;
  cycleTime: IStatMetric;
  workPerPerson: IStatMetric;
  carryOverRate: IStatMetric;
}

export interface AdaptedQuality {
  bugDensityIndex: IStatMetric;
  defectEscapeRate: IStatMetric;
  reworkRatio: IStatMetric;
  criticalBugs: IStatMetric;
  failedScenarios: IStatMetric;
  nonProductionDefects: IStatMetric;
}

export interface AdaptedDelivery {
  featureDeliveryRate: IStatMetric;
}

export function adaptVelocity(
  results: AdoDashboardResult[],
  comparisonEnabled = true,
): AdaptedVelocity {
  const c = comparisonEnabled;
  return {
    throughput: aggSum(results, "velocity_throughput", "throughput", c),
    commitmentReliability: aggAvg(
      results,
      "velocity_throughput",
      "commitment_reliability",
      c,
    ),
    cycleTime: aggAvg(results, "velocity_throughput", "cycle_time", c),
    workPerPerson: aggAvg(
      results,
      "velocity_throughput",
      "completed_work_per_person",
      c,
    ),
    carryOverRate: aggAvg(results, "velocity_throughput", "carry_over_rate", c),
  };
}

export function adaptQuality(
  results: AdoDashboardResult[],
  comparisonEnabled = true,
): AdaptedQuality {
  const c = comparisonEnabled;
  return {
    bugDensityIndex: aggSum(results, "quality", "bug_density_index", c),
    defectEscapeRate: aggAvg(results, "quality", "defect_escape_rate", c),
    reworkRatio: aggAvg(results, "quality", "rework_ratio", c),
    criticalBugs: aggSum(results, "quality", "critical_blocker_bugs", c),
    failedScenarios: aggAvg(results, "quality", "failed_scenarios_per_item", c),
    nonProductionDefects: aggSum(
      results,
      "quality",
      "non_production_defects",
      c,
    ),
  };
}

export function adaptDelivery(
  results: AdoDashboardResult[],
  comparisonEnabled = true,
): AdaptedDelivery {
  return {
    featureDeliveryRate: aggSum(
      results,
      "delivery_progress",
      "feature_delivery_rate",
      comparisonEnabled,
    ),
  };
}

export function adaptBugSeveritySplit(results: AdoMetricResult[]): IBarData {
  if (!results.length) return { labels: [], series: [] };

  const severityKeys = ["1 - Critical", "2 - High", "3 - Medium", "4 - Low"];
  const monthMap = new Map<string, Record<string, number>>();

  for (const r of results) {
    for (const row of r.table_data) {
      const key = typeof row["month"] === "string" ? row["month"] : "";
      if (!key) continue;
      if (!monthMap.has(key)) monthMap.set(key, {});
      const sevs = row["severities"] as
        Record<string, { count: number }> | undefined;
      for (const sk of severityKeys) {
        monthMap.get(key)![sk] =
          (monthMap.get(key)![sk] ?? 0) + (sevs?.[sk]?.count ?? 0);
      }
    }
  }

  const labels = [...monthMap.keys()].sort(naturalSprintSort);
  return {
    labels,
    series: severityKeys.map((sk) => ({
      name: sk,
      values: labels.map((m) => monthMap.get(m)![sk] ?? 0),
    })),
  };
}
